/**
 * "Talk to Hero" voice widget — the marketing-page entry point.
 *
 * GitHub issue #555 ("P0 Bug: Voice AI widget ('Talk to Hero') failing —
 * 'Something went wrong' for new leads"). Per `docs/flows/02-voice-widget.md`
 * / `docs/flows/GATE-113-SIGNOFF.md`, no such component existed anywhere
 * in `apps/web` before this fix — this is the real, first implementation
 * (state machine lives in `@/hooks/useTalkToHeroWidget`, this file is
 * purely presentational).
 *
 * Mounted from `HowItWorks.tsx` (the ticket's `herohire.ai/how-it-works`
 * repro page). Uses `components/ui/dialog.tsx` (new — first modal in this
 * app) rather than a hand-rolled overlay, for the same focus-trap/Escape/
 * overlay-click-to-close/ARIA behavior every other Radix-based primitive
 * in this repo already gets for free.
 */
import { useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useTalkToHeroWidget } from '@/hooks/useTalkToHeroWidget';

export default function TalkToHeroWidget(): JSX.Element {
  const { isOpen, open, close, state, submitEmail } = useTalkToHeroWidget();
  const [email, setEmail] = useState('');

  const isBusy = state.status === 'validating' || state.status === 'connecting';

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    void submitEmail(email);
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(next) => {
        if (next) {
          open();
        } else {
          close();
          setEmail('');
        }
      }}
    >
      <DialogTrigger asChild>
        <Button size="lg" variant="outline">
          Talk to Hero
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Talk to Hero</DialogTitle>
          <DialogDescription>
            Start a live voice conversation with Hero, our AI hiring assistant.
          </DialogDescription>
        </DialogHeader>

        {state.status === 'active' ? (
          <div role="status" className="rounded-md border border-green-600/30 bg-green-50 p-4 text-green-800">
            <p className="font-medium">Connected — Hero is listening…</p>
            <p className="mt-1 text-sm">Go ahead, start talking.</p>
          </div>
        ) : state.status === 'connecting' ? (
          <div role="status" className="rounded-md border bg-muted p-4">
            <p className="font-medium">Connecting to Hero…</p>
          </div>
        ) : (
          // `noValidate`: our own JS validation (`useTalkToHeroWidget`'s
          // `submitEmail`, backed by `@/lib/validation/widget`) is the
          // single source of truth for the "invalid email" message below.
          // Without it, the browser's native HTML5 constraint validation
          // (triggered by `type="email"`) would silently swallow the
          // submit event for malformed input before our handler ever
          // runs, showing a native browser tooltip instead of GH #555's
          // required inline message.
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div className="space-y-1">
              <label htmlFor="talk-to-hero-email" className="text-sm font-medium">
                Email address
              </label>
              <input
                id="talk-to-hero-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isBusy}
                placeholder="you@company.com"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-invalid={state.status === 'invalid-email'}
                aria-describedby={state.status === 'invalid-email' ? 'talk-to-hero-email-error' : undefined}
              />
              {state.status === 'invalid-email' ? (
                <p id="talk-to-hero-email-error" role="alert" className="text-sm text-destructive">
                  {state.message}
                </p>
              ) : null}
              {state.status === 'rate-limited' ? (
                <p role="alert" className="text-sm text-destructive">
                  {state.message}
                </p>
              ) : null}
              {state.status === 'error' ? (
                <p role="alert" className="text-sm text-destructive">
                  {state.message}
                </p>
              ) : null}
            </div>

            <Button type="submit" disabled={isBusy} className="w-full">
              {state.status === 'validating' ? 'Validating…' : 'Start Conversation'}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
